//Custom components

import { Status as ProjectStatuses } from '@stptech/shared-kit/build/constants/project';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { cclfProjectStatus } from '@/constants/Project';

//Services
import { useDashboardData } from '@/hooks/ReactQueryHooks/useDashboardQuery';
//Assets and miscellaneous
import type { DashboardType } from '@/types/Analytics.ts';
import type { TProjectStages } from '@/types/Project.ts';
import Card from '../../UI/Card/index.tsx';
import Table from '../../UI/Table';
import { Widget } from '../constants';
import WidgetLayout from '../WidgetLayout';

const allProjectStatuses = Object.keys(ProjectStatuses).reduce((acc: any, next) => {
  const stageStatus = ProjectStatuses[next as TProjectStages];
  Object.keys(stageStatus).forEach((sk) => {
    acc[sk] = stageStatus[sk as keyof typeof stageStatus];
  });
  return acc;
}, {});

//appending CCLF Project statuses to avoid disruption
const updatedProjectStatuses = { ...allProjectStatuses, ...cclfProjectStatus };

export interface ProjectStatusProps {
  status: string;
  _count: number;
}

interface ProjectStatusSummaryWidgetProps {
  dashboard: DashboardType;
}

const ProjectStatusSummaryWidget = ({ dashboard }: ProjectStatusSummaryWidgetProps) => {
  const [perPage, setPerPage] = useState<number>(5);
  const [page, setPage] = useState<number>(1);

  const fetchProjectStatusSummaryWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECT_STATUS_SUMMARY,
  });

  const projectStatusSummaryWidgetData = fetchProjectStatusSummaryWidgetData?.data?.data;

  const projectStatus = useMemo(() => {
    if (!Array.isArray(projectStatusSummaryWidgetData)) {
      return [] as ProjectStatusProps[];
    }

    return projectStatusSummaryWidgetData.filter((s: ProjectStatusProps) => s._count !== 0);
  }, [projectStatusSummaryWidgetData]);

  const totalPage = Math.max(1, Math.ceil(projectStatus.length / perPage));

  const pagedData = useMemo(() => {
    const start = (page - 1) * perPage;
    return projectStatus.slice(start, start + perPage);
  }, [projectStatus, page, perPage]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPage));
  }, [totalPage]);

  const nextPageHandler = () => {
    setPage((prev) => Math.min(prev + 1, totalPage));
  };

  const previousPageHandler = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const tableColumns: ColumnDef<ProjectStatusProps>[] = [
    {
      id: 'status',
      header: 'STATUS',
      accessorKey: 'status',
      cell: (props) => {
        const status = props.getValue() as string;
        return updatedProjectStatuses[status] ?? status;
      },
    },
    {
      id: 'count',
      header: () => <div className='text-end'>PROJECTS</div>,
      accessorKey: '_count',
      cell: (props) => {
        const value = props.getValue() as number;
        return (
          <div className='text-end font-semibold'>
            <span>{value}</span>
          </div>
        );
      },
    },
  ];

  const tablePagination = {
    page: page - 1,
    pageSize: perPage,
    pageCount: totalPage,
    currentPage: page,
    setPageSize: (v: number) => {
      setPerPage(v);
      setPage(1);
    },
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  return (
    <WidgetLayout dashboard={dashboard} widget={Widget.PROJECT_STATUS_SUMMARY}>
      {Array.isArray(projectStatusSummaryWidgetData) && (
        <Card title='Project Status Summary' error={fetchProjectStatusSummaryWidgetData.isError}>
          <div className='w-full overflow-x-auto'>
            <Table cols={tableColumns} tableData={pagedData} pagination={tablePagination} />
          </div>
        </Card>
      )}
    </WidgetLayout>
  );
};

export default ProjectStatusSummaryWidget;
